import { format, parseISO } from "date-fns";
import { joinDisplayName } from "@/utils/name.util";
import { formatMemberPhoneNumber } from "@/utils/phone.util";
import { shouldDisplayTitle } from "@/utils/title.util";
import type {
    AdminDetails,
    ContactDetails,
    PersonalDetails,
    TravelDetails,
} from "./data/profile";
import {
    formatAddress,
    formatInterestsDisplay,
    formatPreferredCommunicationChannel,
    formatTopCities,
    listMissingLabels,
} from "./profileUtils";

type FieldRowData = { label: string; value: string };

export function personalRows(personal: PersonalDetails) {
    const interests = formatInterestsDisplay(personal);
    const topLocations = formatTopCities(personal.topCities);
    return compactRows([
        {
            label: "Name",
            value: joinDisplayName(
                personal.firstName,
                personal.lastName,
                shouldDisplayTitle(personal.title) ? personal.title : undefined,
            ),
        },
        personal.greeting ? { label: "Greeting", value: personal.greeting } : undefined,
        personal.dateOfBirth
            ? {
                  label: "Date of birth",
                  value: format(parseISO(personal.dateOfBirth), "d MMM yyyy"),
              }
            : undefined,
        personal.occupation
            ? { label: "Occupation", value: personal.occupation }
            : undefined,
        personal.memberships
            ? { label: "Memberships", value: personal.memberships }
            : undefined,
        topLocations ? { label: "Top locations", value: topLocations } : undefined,
        interests ? { label: "Interests", value: interests } : undefined,
    ]);
}

export function personalMissing(personal: PersonalDetails) {
    return listMissingLabels([
        { label: "Highlight", present: !!personal.personalHighlight },
        { label: "Must know", present: !!personal.personalMustKnow },
        { label: "Greeting", present: !!personal.greeting },
        { label: "Date of birth", present: !!personal.dateOfBirth },
        { label: "Memberships", present: !!personal.memberships },
        { label: "Top locations", present: !!personal.topCities?.length },
        { label: "Interests", present: !!formatInterestsDisplay(personal) },
        { label: "Occupation", present: !!personal.occupation },
    ]);
}

export function contactRows(contact: ContactDetails) {
    const address = formatAddress(contact);
    const channel = formatPreferredCommunicationChannel(contact);
    return compactRows([
        contact.email ? { label: "Email", value: contact.email } : undefined,
        contact.secondaryEmail
            ? { label: "Secondary", value: contact.secondaryEmail }
            : undefined,
        contact.phoneNumber
            ? { label: "Phone", value: formatMemberPhoneNumber(contact.phoneNumber) }
            : undefined,
        contact.secondaryPhone
            ? {
                  label: "Secondary",
                  value: formatMemberPhoneNumber(contact.secondaryPhone),
              }
            : undefined,
        channel ? { label: "Preferred channel", value: channel } : undefined,
        address ? { label: "Address", value: address } : undefined,
    ]);
}

export function adminRows(admin: AdminDetails) {
    return compactRows([
        admin.iosAppVersion
            ? { label: "iOS app version", value: admin.iosAppVersion }
            : undefined,
        admin.iosVersion
            ? { label: "iOS version", value: admin.iosVersion }
            : undefined,
        admin.iosDevice ? { label: "iOS device", value: admin.iosDevice } : undefined,
        admin.pushNotifications
            ? { label: "Push notifications", value: admin.pushNotifications }
            : undefined,
        admin.androidAppVersion
            ? { label: "Android App version", value: admin.androidAppVersion }
            : undefined,
        admin.androidVersion
            ? { label: "Android version", value: admin.androidVersion }
            : undefined,
        admin.androidDevice
            ? { label: "Android device", value: admin.androidDevice }
            : undefined,
        admin.androidWebNotifications
            ? {
                  label: "Android/Web notifications",
                  value: admin.androidWebNotifications,
              }
            : undefined,
    ]);
}

export function adminMissing(admin: AdminDetails) {
    return listMissingLabels([
        { label: "iOS app version", present: !!admin.iosAppVersion },
        { label: "iOS version", present: !!admin.iosVersion },
        { label: "iOS device", present: !!admin.iosDevice },
        { label: "Android App version", present: !!admin.androidAppVersion },
        { label: "Android version", present: !!admin.androidVersion },
        { label: "Android device", present: !!admin.androidDevice },
        { label: "Push notifications", present: !!admin.pushNotifications },
        {
            label: "Android/Web notifications",
            present: !!admin.androidWebNotifications,
        },
        { label: "Philanthropy", present: !!admin.philanthropy },
    ]);
}

export function travelRows(travel: TravelDetails) {
    return compactRows([
        travel.nationality
            ? { label: "Nationality", value: travel.nationality }
            : undefined,
        travel.passportName
            ? { label: "Passport name", value: travel.passportName }
            : undefined,
        travel.passportNumber
            ? { label: "Passport number", value: travel.passportNumber }
            : undefined,
        travel.passportExpiryDate
            ? {
                  label: "Passport expires",
                  value: format(parseISO(travel.passportExpiryDate), "d MMM yyyy"),
              }
            : undefined,
        travel.airlinePreferences
            ? { label: "Airline preference", value: travel.airlinePreferences }
            : undefined,
        travel.frequentFlyerNumber
            ? { label: "Frequent flyer", value: travel.frequentFlyerNumber }
            : undefined,
    ]);
}

export function travelMissing(travel: TravelDetails) {
    return listMissingLabels([
        { label: "Must know", present: !!travel.travelMustKnow },
        {
            label: "Future plans",
            present: !!(travel.futurePlan || travel.onboardingFuturePlan),
        },
        { label: "Nationality", present: !!travel.nationality },
        { label: "Passport name", present: !!travel.passportName },
        { label: "Passport number", present: !!travel.passportNumber },
        { label: "Passport expires", present: !!travel.passportExpiryDate },
        { label: "Airline preference", present: !!travel.airlinePreferences },
        { label: "Frequent flyer", present: !!travel.frequentFlyerNumber },
    ]);
}

function compactRows(rows: (FieldRowData | undefined)[]): FieldRowData[] {
    return rows.filter((row): row is FieldRowData => !!row);
}
